import React, { useMemo } from 'react';
import Localize from 'react-intl-universal';
import { useNavigate } from 'react-router-dom';

import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Container from '@mui/material/Container';
import { styled, useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';

import EmblaCarousel from '@components/EmblaCarousel/EmblaCarousel';
import NewsCard from '@components/NewsCard/NewsCard';
import { getHomePageText } from '@components/NewsCard/Util';
import RichTextEditor from '@components/RichTextEditor/RichTextEditor';
import { routes } from '@navigation/Constants';
import useNews from '@pages/AllNews/hooks/useNews';

import useHomePageData from './useHomePage';

const StyledBox = styled(Box)((props) => {
  const HomeImage = new URL(`../../assets/Images/${props?.image}`, import.meta.url).href;
  return {
    minHeight: '100vh',
    marginTop: '-2.92rem',
    marginBottom: '-2.5rem',
    width: '100%',
    height: 'auto',
    backgroundImage: `url(${HomeImage})`,
    backgroundPosition: 'top',
    backgroundRepeat: 'no-repeat',
    backgroundSize: 'cover'
  };
});
const StyledTypography = styled(Typography)(({ theme }) => ({
  color: theme.palette.common.white,
  paddingBottom: '1rem'
}));

const OPTIONS = { align: 'start' };
const NO_NEWS_FOR_VIEW = 6;

const Home = ({ configuration }) => {
  const { homePageData } = useHomePageData();
  const navigate = useNavigate();

  const config = configuration?.MainCustomerLoyaltyEventItem;
  const theme = useTheme();

  const memoizedImageSource = useMemo(() => {
    if (!config?.content?.img) return null;

    try {
      return new URL(`../../assets/Images/${config.content.img}`, import.meta.url).href;
    } catch (e) {
      console.warn('Image not found:', config.content.img);
      return null;
    }
  }, [config?.content?.img]);

  const { newsData, isLoading } = useNews(configuration, NO_NEWS_FOR_VIEW);

  const sortedNews = newsData ? [...newsData].sort((a, b) => b.important - a.important) : [];

  const getNews = () =>
    sortedNews.map((news, index) => (
      <NewsCard key={index} news={news} memoizedImageSource={memoizedImageSource} />
    ));

  const onClickAdditionalBtn = () => {
    navigate(routes.path.allNews, {
      state: { from: '', path: '' }
    });
  };

  return (
    <StyledBox component='main' image={configuration?.login?.backgroundImageName}>
      <Container sx={{ pt: 8, pb: 4 }}>
        <RichTextEditor
          initialValue={getHomePageText(homePageData) || Localize.get('EventDetails.noTopics')}
          disabled
          isToolbarHidden
          customStyles={{
            minHeight: '50px',
            color: `${theme.palette.common.white}`,
            height: '25vh'
          }}
        />

        <Box sx={{ width: { xs: 'inherit', md: '80%' }, mt: 5 }}>
          <StyledTypography variant='h5' component='h2'>
            {Localize.get('HomePage.news')}
          </StyledTypography>

          {isLoading && (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '50vh'
              }}
            >
              <CircularProgress size={60} />
            </Box>
          )}

          {!isLoading &&
            (getNews().length > 0 ? (
              <EmblaCarousel
                slides={getNews()}
                options={OPTIONS}
                isAdditionalBtn={true}
                onClickAdditionalBtn={onClickAdditionalBtn}
              />
            ) : (
              <StyledTypography>{Localize.get('HomePage.noNews')}</StyledTypography>
            ))}
        </Box>
      </Container>
    </StyledBox>
  );
};

export default Home;
